import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { differenceInDays, differenceInMonths, format } from 'date-fns';
import { upperFirst } from 'lodash';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

import { DataGrid } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import { Button } from '@dashboard/components/ui/button';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import { useMerchantBillingHistory } from '@dashboard/hooks/queries/useAdminQueries';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { formatPrice } from '@dashboard/services/formatting';
import { getBillingPeriod } from '@dashboard/services/plans';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';

type BillingPlan = {
  name: string;
  handle: string | null;
  bracket: number | null;
  billingPeriod: string | null;
  amount: number | null;
  currency: string | null;
  interval: string | null;
  intervalCount: number;
};

type BillingEvent =
  | { type: 'signed_up'; date: string; trialEndsAt: string | null }
  | { type: 'comped'; date: string; reason: string | null }
  | {
      type: 'subscribed';
      date: string;
      plan: BillingPlan | null;
      resubscribed: boolean;
      trialEndsAt: string | null;
    }
  | {
      type: 'plan_changed';
      date: string;
      plan: BillingPlan;
      from: BillingPlan;
      direction: 'upgrade' | 'downgrade' | 'billing_period' | 'price';
    }
  | { type: 'cancel_scheduled'; date: string; endsAt: string; reason: string | null }
  | { type: 'ended'; date: string; reason: string | null }
  | { type: 'checkout_not_completed'; date: string; plan: BillingPlan | null };

type BillingSubscription = {
  id: string;
  status: string;
  plan: BillingPlan | null;
  startedAt: string;
  endedAt: string | null;
  cancelAt: string | null;
  currency: string | null;
  amountPaid: number;
  amountRefunded: number;
  events: BillingEvent[];
};

type BillingInvoice = {
  id: string;
  number: string | null;
  status: string | null;
  kind: 'subscription' | 'overage' | 'other';
  billingReason: string | null;
  description: string | null;
  plan: BillingPlan | null;
  createdAt: string;
  currency: string;
  total: number;
  amountPaid: number;
  amountRefunded: number;
  failedPayments: number;
  invoicePdf: string | null;
};

type BillingTimelineItem =
  | { type: 'event'; date: string; event: BillingEvent }
  | { type: 'subscription'; date: string; subscription: BillingSubscription };

type BillingHistory = {
  stripeCustomerId: string | null;
  totals: { currency: string; amountPaid: number; amountRefunded: number; invoiceCount: number }[];
  firstPaidAt: string | null;
  timeline: BillingTimelineItem[];
  invoices: BillingInvoice[];
};

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Past due',
  unpaid: 'Unpaid',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  incomplete_expired: 'Not completed',
  paused: 'Paused',
};

const INVOICE_PREVIEW_COUNT = 5;

const DOT_COLORS: Record<string, string> = {
  active: 'bg-green-500',
  trialing: 'bg-green-500',
  past_due: 'bg-orange-400',
  unpaid: 'bg-orange-400',
};

const BILLING_REASON_LABELS: Record<string, string> = {
  subscription_create: 'First payment',
  subscription_cycle: 'Renewal',
  subscription_update: 'Plan change',
  subscription_threshold: 'Usage threshold',
};

const formatDay = (date: string) => format(new Date(date), 'd MMM, yyyy');

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

function formatDuration(start: string, end: string | null) {
  const from = new Date(start);
  const to = end ? new Date(end) : new Date();
  const months = differenceInMonths(to, from);

  if (months < 1) return plural(differenceInDays(to, from), 'day');
  if (months < 12) return plural(months, 'month');

  const years = Math.floor(months / 12);
  return [plural(years, 'year'), months % 12 ? plural(months % 12, 'month') : null]
    .filter(Boolean)
    .join(' ');
}

function planLabel(plan: BillingPlan | null) {
  if (!plan) return 'Unknown plan';
  return plan.handle ? `${plan.name} ${getBillingPeriod(plan.billingPeriod)}` : plan.name;
}

function priceLabel(plan: BillingPlan | null) {
  if (plan?.amount == null || !plan.currency) return null;
  return getPriceLabel({
    amount: plan.amount,
    currency: plan.currency,
    interval: plan.interval,
    intervalCount: plan.intervalCount,
    type: 'subscription',
  });
}

const join = (parts: (string | null | false | undefined)[], separator = ' · ') =>
  parts.filter(Boolean).join(separator) || null;

const reasonLabel = (reason: string | null) => (reason ? `Reason: ${reason}` : null);

function describeEvent(event: BillingEvent): { title: string; detail: string | null } {
  switch (event.type) {
    case 'signed_up':
      return {
        title: 'Signed up',
        detail: event.trialEndsAt
          ? `Trial ${new Date(event.trialEndsAt) < new Date() ? 'ended' : 'ends'} ${formatDay(event.trialEndsAt)}`
          : null,
      };
    case 'comped':
      return { title: 'Plan comped', detail: reasonLabel(event.reason) };
    case 'subscribed':
      return {
        title: `${event.resubscribed ? 'Resubscribed' : 'Subscribed'} to ${planLabel(event.plan)}`,
        detail: join([
          priceLabel(event.plan),
          event.trialEndsAt && `Trial until ${formatDay(event.trialEndsAt)}`,
        ]),
      };
    case 'plan_changed': {
      const titles = {
        upgrade: `Upgraded to ${planLabel(event.plan)}`,
        downgrade: `Downgraded to ${planLabel(event.plan)}`,
        billing_period: `Switched to ${getBillingPeriod(event.plan.billingPeriod)} billing`,
        price: `Price changed to ${priceLabel(event.plan) ?? planLabel(event.plan)}`,
      };
      return {
        title: titles[event.direction],
        detail: `From ${join([planLabel(event.from), priceLabel(event.from)], ', ')}`,
      };
    }
    case 'cancel_scheduled':
      return { title: 'Canceled at period end', detail: reasonLabel(event.reason) };
    case 'ended':
      return { title: 'Ended', detail: reasonLabel(event.reason) };
    case 'checkout_not_completed':
      return {
        title: `Checkout for ${planLabel(event.plan)} not completed`,
        detail: 'The first payment did not go through',
      };
  }
}

function EventRow({ event, action }: { event: BillingEvent; action?: React.ReactNode }) {
  const { title, detail } = describeEvent(event);

  return (
    <div className="flex items-start gap-x-4">
      <time
        dateTime={event.date}
        className="w-28 shrink-0 text-sm text-gray-500 dark:text-muted-foreground"
      >
        {formatDay(event.date)}
      </time>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-foreground">{title}</p>
        {detail && <p className="text-sm text-gray-500 dark:text-muted-foreground">{detail}</p>}
      </div>
      {action}
    </div>
  );
}

function StripeLink({ path, label }: { path: string; label: string }) {
  return (
    <Tooltip label={label} asChild>
      <Button asChild variant="outline" size="icon-sm">
        <a
          href={getStripeDashboardUrl(path)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
        >
          <ArrowTopRightOnSquareIcon />
        </a>
      </Button>
    </Tooltip>
  );
}

const isNotCompleted = (subscription: BillingSubscription) =>
  subscription.status === 'incomplete_expired' || subscription.status === 'incomplete';

function SubscriptionCard({ subscription }: { subscription: BillingSubscription }) {
  const { status, startedAt, endedAt, cancelAt, currency, amountPaid, amountRefunded } =
    subscription;
  const endsAt = endedAt ?? cancelAt;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-border p-4">
      <div className="flex items-start justify-between gap-x-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-foreground">
              {planLabel(subscription.plan)}
            </h3>
            <StatusBadge
              status={status}
              label={SUBSCRIPTION_STATUS_LABELS[status] ?? upperFirst(status)}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-muted-foreground">
            {formatDay(startedAt)} - {endsAt ? formatDay(endsAt) : 'now'} (
            {formatDuration(startedAt, endsAt)})
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-x-3 text-sm">
          {amountPaid > 0 && currency && (
            <span>
              Paid {formatPrice({ amount: amountPaid, currency })}
              {amountRefunded > 0 && (
                <span className="text-gray-500 dark:text-muted-foreground">
                  , {formatPrice({ amount: amountRefunded, currency })} refunded
                </span>
              )}
            </span>
          )}
          <StripeLink
            path={`/subscriptions/${subscription.id}`}
            label="View subscription in Stripe"
          />
        </div>
      </div>

      <ul className="mt-4 space-y-2 border-t border-gray-100 dark:border-border pt-4">
        {subscription.events.map((event, index) => (
          <li key={`${event.type}-${index}`}>
            <EventRow event={event} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Summary({ history }: { history: BillingHistory }) {
  if (!history.stripeCustomerId) {
    return (
      <p className="text-sm text-gray-500 dark:text-muted-foreground">
        No Stripe customer, so no subscriptions or invoices to show.
      </p>
    );
  }
  if (!history.totals.length) {
    return <p className="text-sm text-gray-500 dark:text-muted-foreground">No paid invoices.</p>;
  }

  const paid = history.totals.map(total =>
    formatPrice({ amount: total.amountPaid, currency: total.currency }),
  );
  const refunded = history.totals
    .filter(total => total.amountRefunded > 0)
    .map(total => formatPrice({ amount: total.amountRefunded, currency: total.currency }));
  const invoiceCount = history.totals.reduce((sum, total) => sum + total.invoiceCount, 0);

  return (
    <p className="text-sm">
      Paid {paid.join(' + ')} over {plural(invoiceCount, 'invoice')}
      {history.firstPaidAt && ` since ${formatDay(history.firstPaidAt)}`}
      {refunded.length > 0 && `, ${refunded.join(' + ')} refunded`}.
    </p>
  );
}

function invoiceStatus(invoice: BillingInvoice) {
  if (invoice.status === 'paid') {
    if (invoice.amountRefunded > 0 && invoice.amountRefunded >= invoice.amountPaid) {
      return { status: 'refunded', label: 'Refunded' };
    }
    if (invoice.amountRefunded > 0)
      return { status: 'partially_refunded', label: 'Partially refunded' };
    return { status: 'paid', label: 'Paid' };
  }
  if (invoice.status === 'open') {
    return invoice.failedPayments
      ? { status: 'past_due', label: 'Payment failed' }
      : { status: 'open', label: 'Open' };
  }
  if (invoice.status === 'uncollectible') return { status: 'failed', label: 'Uncollectible' };
  return { status: invoice.status ?? '', label: upperFirst(invoice.status ?? '') };
}

function invoiceDescription(invoice: BillingInvoice) {
  if (invoice.kind === 'subscription') {
    return {
      title: invoice.plan ? planLabel(invoice.plan) : (invoice.description ?? 'Subscription'),
      detail: invoice.billingReason ? (BILLING_REASON_LABELS[invoice.billingReason] ?? null) : null,
    };
  }
  if (invoice.kind === 'overage') return { title: 'Overage fee', detail: invoice.description };
  return { title: invoice.description ?? 'One-off invoice', detail: null };
}

function InvoicesTable({
  invoices,
  isLoading = false,
}: {
  invoices: BillingInvoice[];
  isLoading?: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const canExpand = invoices.length > INVOICE_PREVIEW_COUNT;
  const visibleInvoices =
    showAll || !canExpand ? invoices : invoices.slice(0, INVOICE_PREVIEW_COUNT);

  const columns = useMemo<ColumnDef<BillingInvoice, unknown>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: ({ row }) => (
          <span className="whitespace-nowrap">{formatDay(row.original.createdAt)}</span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'number',
        header: 'Number',
        cell: ({ row }) => <span className="whitespace-nowrap">{row.original.number}</span>,
        enableSorting: false,
      },
      {
        id: 'description',
        header: 'Description',
        cell: ({ row }) => {
          const { title, detail } = invoiceDescription(row.original);
          return (
            <div>
              <div>{title}</div>
              {detail && <div className="text-gray-500 dark:text-muted-foreground">{detail}</div>}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'total',
        header: 'Amount',
        cell: ({ row }) => {
          const invoice = row.original;
          return (
            <div className="whitespace-nowrap">
              <div>{formatPrice({ amount: invoice.total, currency: invoice.currency })}</div>
              {invoice.amountRefunded > 0 && (
                <div className="text-gray-500 dark:text-muted-foreground">
                  {formatPrice({ amount: invoice.amountRefunded, currency: invoice.currency })}{' '}
                  refunded
                </div>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const invoice = row.original;
          const { status, label } = invoiceStatus(invoice);
          return (
            <div>
              <StatusBadge status={status} label={label} />
              {invoice.status === 'paid' && invoice.failedPayments > 0 && (
                <div className="mt-1 text-gray-500 dark:text-muted-foreground">
                  After {plural(invoice.failedPayments, 'failed payment')}
                </div>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const invoice = row.original;
          return (
            <div className="flex gap-x-2 justify-end">
              {invoice.invoicePdf && (
                <Button asChild variant="outline" size="sm">
                  <a href={invoice.invoicePdf} target="_blank" rel="noopener noreferrer">
                    Download
                    <ArrowDownTrayIcon />
                  </a>
                </Button>
              )}
              <StripeLink path={`/invoices/${invoice.id}`} label="View invoice in Stripe" />
            </div>
          );
        },
        enableSorting: false,
        meta: { className: 'text-right' },
      },
    ],
    [],
  );

  return (
    <div className="space-y-3">
      <DataGrid
        columns={columns}
        data={visibleInvoices}
        isLoading={isLoading}
        pageSize={isLoading ? INVOICE_PREVIEW_COUNT : undefined}
        emptyState={
          <EmptyState>
            <EmptyState.Subtitle>No invoices.</EmptyState.Subtitle>
          </EmptyState>
        }
      />
      {canExpand && (
        <Button variant="outline" size="sm" onClick={() => setShowAll(value => !value)}>
          {showAll ? 'Show less' : `Show all ${invoices.length} invoices`}
        </Button>
      )}
    </div>
  );
}

const SubscriptionCardSkeleton = () => (
  <div className="rounded-lg border border-gray-200 dark:border-border p-4">
    <div className="flex items-start justify-between gap-x-4">
      <div className="space-y-2">
        <div className="flex items-center gap-x-2">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-5 w-14" />
        </div>
        <Skeleton className="h-4 w-56" />
      </div>
      <Skeleton className="h-8 w-28" />
    </div>
    <div className="mt-4 space-y-3 border-t border-gray-100 dark:border-border pt-4">
      {[0, 1].map(index => (
        <div key={index} className="flex gap-x-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-56" />
        </div>
      ))}
    </div>
  </div>
);

const BillingHistorySkeleton = () => (
  <>
    <div aria-busy="true">
      <h2 className="mb-4">Subscription history</h2>
      <div className="space-y-6">
        <Skeleton className="h-4 w-80" />
        <div className="ml-1.5 space-y-6 border-l border-gray-200 dark:border-border">
          <div className="ml-6">
            <SubscriptionCardSkeleton />
          </div>
          <div className="ml-6">
            <SubscriptionCardSkeleton />
          </div>
          <div className="ml-6 flex gap-x-4 border border-transparent px-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
      </div>
    </div>

    <div>
      <h2 className="mb-4">Invoices</h2>
      <InvoicesTable invoices={[]} isLoading />
    </div>
  </>
);

export default function MerchantBillingHistory({ sellerId }: { sellerId: string }) {
  const { data, isLoading, error } = useMerchantBillingHistory(sellerId);
  const history = data as BillingHistory | undefined;

  if (isLoading) return <BillingHistorySkeleton />;

  if (error || !history) {
    return (
      <div>
        <h2 className="mb-4">Subscription history</h2>
        <p className="text-sm text-red-600 dark:text-red-400">
          {(error as { message?: string } | null)?.message || 'Could not load billing history.'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div>
        <h2 className="mb-4">Subscription history</h2>
        <div className="space-y-6">
          <Summary history={history} />

          <ol className="relative ml-1.5 space-y-6 border-l border-gray-200 dark:border-border">
            {history.timeline.map(item => (
              <li
                key={
                  item.type === 'subscription'
                    ? item.subscription.id
                    : `${item.event.type}-${item.date}`
                }
                className="ml-6"
              >
                {item.type === 'subscription' && !isNotCompleted(item.subscription) ? (
                  <>
                    <span
                      className={clsx(
                        'absolute -left-[5px] mt-6 h-2.5 w-2.5 rounded-full ring-4 ring-white dark:ring-background',
                        DOT_COLORS[item.subscription.status] || 'bg-gray-300 dark:bg-accent',
                      )}
                    />
                    <SubscriptionCard subscription={item.subscription} />
                  </>
                ) : (
                  <>
                    <span className="absolute -left-[5px] mt-2 h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-accent ring-4 ring-white dark:ring-background" />
                    {/* Matches the card's border and padding, so every date sits in one column. */}
                    <div className="border border-transparent px-4">
                      {item.type === 'subscription' ? (
                        <EventRow
                          event={item.subscription.events[0]}
                          action={
                            <StripeLink
                              path={`/subscriptions/${item.subscription.id}`}
                              label="View subscription in Stripe"
                            />
                          }
                        />
                      ) : (
                        <EventRow event={item.event} />
                      )}
                    </div>
                  </>
                )}
              </li>
            ))}
          </ol>
        </div>
      </div>

      {history.stripeCustomerId && (
        <div>
          <h2 className="mb-4">Invoices ({history.invoices.length})</h2>
          <InvoicesTable invoices={history.invoices} />
        </div>
      )}
    </>
  );
}
